import React, { useState, useEffect } from 'react';
import {
  Wrench, Plus, Trash2, Edit2, Loader2, Check, X,
  Eye, EyeOff, GripVertical, Save, RotateCcw, Hammer
} from 'lucide-react';
import { travauxAPI } from '../../services/api';

const WidgetTravauxConfig = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingIcone, setEditingIcone] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [newType, setNewType] = useState({
    code: '',
    nom: '',
    icone: '🔧'
  });

  const iconesDisponibles = ['🚿', '🪟', '🧱', '🔥', '🏠', '🔧', '⚡', '💡', '🚰', '🎨', '🛠️', '🏗️'];

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const res = await travauxAPI.getTypes(true);
      setTypes(res.data || []);
    } catch (error) {
      console.error('Erreur chargement types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newType.code || !newType.nom) return;

    setSaving(true);
    try {
      await travauxAPI.createType({
        code: newType.code.toLowerCase().replace(/\s+/g, '_'),
        nom: newType.nom,
        icone: newType.icone,
        ordre: types.length
      });
      await loadTypes();
      setNewType({ code: '', nom: '', icone: '🔧' });
      setShowAddForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Supprimer ce type de travaux ?')) return;

    setDeleting(typeId);
    try {
      await travauxAPI.deleteType(typeId);
      await loadTypes();
    } catch (error) {
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleVisibility = async (type) => {
    setTogglingVisibility(type.id);
    try {
      await travauxAPI.updateType(type.id, { visible: !type.visible });
      await loadTypes();
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setTogglingVisibility(null);
    }
  };

  const startEditing = (type) => {
    setEditingType(type.id);
    setEditingName(type.nom);
    setEditingIcone(type.icone);
  };

  const saveEditing = async () => {
    if (!editingName.trim() || !editingType) return;

    setSaving(true);
    try {
      await travauxAPI.updateType(editingType, { 
        nom: editingName.trim(),
        icone: editingIcone
      });
      await loadTypes();
      setEditingType(null);
      setEditingName('');
      setEditingIcone('');
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditingType(null);
    setEditingName('');
    setEditingIcone('');
  };

  // Drag & Drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newTypes = [...types];
    const draggedItem = newTypes[draggedIndex];
    newTypes.splice(draggedIndex, 1);
    newTypes.splice(index, 0, draggedItem);

    newTypes.forEach((t, idx) => {
      t.ordre = idx;
    });

    setTypes(newTypes);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < types.length; i++) {
        await travauxAPI.updateType(types[i].id, { ordre: i });
      }
      setHasChanges(false);
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const visibleCount = types.filter(t => t.visible !== false).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Hammer className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Types de Travaux</h2>
              <p className="text-orange-100">Configurez les types de travaux pour les devis</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            {visibleCount}/{types.length} visibles
          </span>
        </div>
      </div>

      {/* Liste des types */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Gérez les types de travaux disponibles. Glissez-déposez pour réordonner.
          </p>

          <div className="space-y-2 mb-4">
            {types.map((type, index) => (
              <div
                key={type.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition cursor-move ${
                  draggedIndex === index
                    ? 'border-orange-400 bg-orange-50 shadow-lg'
                    : type.visible !== false
                      ? 'border-orange-200 bg-orange-50 hover:border-orange-300'
                      : 'border-gray-200 bg-gray-100 hover:border-gray-300'
                }`}
              >
                {/* Handle */}
                <div className="text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-5 h-5" />
                </div>

                {/* Icône */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
                  type.visible !== false ? 'bg-orange-100' : 'bg-gray-200'
                }`}>
                  {type.icone || '🔧'}
                </div>

                {/* Nom */}
                <div className="flex-1">
                  {editingType === type.id ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={editingIcone}
                        onChange={(e) => setEditingIcone(e.target.value)}
                        className="w-16 px-2 py-1 border border-orange-300 rounded text-center"
                      >
                        {iconesDisponibles.map(ic => (
                          <option key={ic} value={ic}>{ic}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditing();
                          if (e.key === 'Escape') cancelEditing();
                        }}
                      />
                      <button
                        onClick={saveEditing}
                        disabled={saving}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <span className={`font-medium ${type.visible !== false ? 'text-gray-900' : 'text-gray-500'}`}>
                        {type.nom}
                      </span>
                      {type.visible === false && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Masqué
                        </span>
                      )}
                      <div className="text-xs text-gray-500">Code: {type.code}</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {editingType !== type.id && (
                    <button
                      onClick={() => startEditing(type)}
                      className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleVisibility(type)}
                    disabled={togglingVisibility === type.id}
                    className={`p-2 rounded-lg transition ${
                      type.visible !== false
                        ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    title={type.visible !== false ? 'Masquer' : 'Afficher'}
                  >
                    {togglingVisibility === type.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : type.visible !== false ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(type.id)}
                    disabled={deleting === type.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Supprimer"
                  >
                    {deleting === type.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}

            {types.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Wrench className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun type de travaux configuré</p>
              </div>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <form onSubmit={handleAddType} className="bg-orange-50 rounded-lg p-4 border border-orange-200 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">➕ Nouveau type de travaux</h4>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                  <select
                    value={newType.icone}
                    onChange={(e) => setNewType({ ...newType, icone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-xl"
                  >
                    {iconesDisponibles.map(ic => (
                      <option key={ic} value={ic}>{ic}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <input
                    type="text"
                    value={newType.code}
                    onChange={(e) => setNewType({ ...newType, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="ex: climatisation"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={newType.nom}
                    onChange={(e) => setNewType({ ...newType, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="ex: Climatisation"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Créer
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-orange-300 rounded-lg text-orange-600 hover:border-orange-400 hover:bg-orange-50 transition flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Ajouter un type de travaux
            </button>
          )}

          {/* Bouton sauvegarder l'ordre */}
          {hasChanges && (
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={saveOrder}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer l'ordre
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  loadTypes();
                  setHasChanges(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetTravauxConfig;
